import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import {
  CheckCircle2,
  Plus,
  Users as UsersIcon,
  XCircle,
  ShieldCheck,
  UploadCloud,
  UserCog,
  User as UserIcon,
  MapPinned,
  Key,
  Pencil,
  Power,
  Trash2,
  CircleDot,
} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast, generateRandomNumber, formatDateTime, registeredUpdatedTitle} from "../../utils/utility";
import {buildPaginationParams, DEFAULT_PAGE_SIZE, extractPaginationHeaders, PAGE_SIZE_OPTIONS} from "../../utils/paginationUtils";
import {logoutFun} from "../../component/HomeSideNavBar";
import {hasPermission} from "../../utils/rolePermissions";

import Button from "../../components/ui/Button";
import Pill from "../../components/ui/Pill";
import Avatar from "../../components/ui/Avatar";
import KebabMenu from "../../components/ui/KebabMenu";
import EmptyState from "../../components/ui/EmptyState";
import Spinner from "../../components/ui/Spinner";
import PageHeader from "../../components/layout/PageHeader";
import KpiTile, {KpiRow} from "../../components/data/KpiTile";
import FilterBar from "../../components/data/FilterBar";
import {tableStyles} from "../../components/data/dataTableTheme";
import Select from "../../components/ui/Select";
import {ROLE_LABELS, ROLE_OPTIONS, ASSIGNABLE_ROLE_OPTIONS, isSuspended} from "./userRoles";
import UserSlideOver from "./UserSlideOver";
import ResetPasswordSlideOver from "./ResetPasswordSlideOver";
import BulkUploadSlideOver from "../../components/data/BulkUploadSlideOver";

/** Bulk-import template: column contract + per-cell validation. */
// Bulk import is a creation path (goes through the same registerUser endpoint as "New user"), so
// it uses the assignable-only role list — MOBI_TRACK excluded, same reasoning as UserSlideOver.
const ROLE_VALUES = ASSIGNABLE_ROLE_OPTIONS.map((r) => r.value);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BULK_USER_COLUMNS = [
  {key: "fullName", label: "Full Name", required: true},
  // Relaxed contract: at least one of email / phone per row; each validated
  // only when present. Email and phone are unique across the file (and the
  // backend enforces uniqueness against the DB per row).
  {key: "email", label: "Email", required: false, unique: true,
    validate: (v) => (EMAIL_RE.test(v) ? null : "must be a valid email address")},
  {key: "phoneNumber", label: "Phone", required: false, unique: true,
    requiredIf: (row) => !String(row.email || "").length,
    validate: (v) => (/^[+\d][\d\s-]{6,}$/.test(v) ? null : "must be a valid phone number")},
  {key: "userRole", label: "Role", required: true,
    // Accept the enum value (CLIENT_MANAGER) or the display label (Manager). Matched only
    // against the assignable roles — "Track Point"/"MOBI_TRACK" deliberately falls through
    // unnormalized so it fails validate() below with a clear "must be one of ..." instead of
    // silently resolving to a role this import path can't safely create.
    normalize: (v) => {
      const hit = ASSIGNABLE_ROLE_OPTIONS.find(
        (r) => r.value === v.toUpperCase().replace(/\s+/g, "_") || r.label.toLowerCase() === v.toLowerCase()
      );
      return hit ? hit.value : v;
    },
    validate: (v) => (ROLE_VALUES.includes(v) ? null : `must be one of ${ASSIGNABLE_ROLE_OPTIONS.map((r) => r.label).join(", ")}`)},
  {key: "city", label: "City", required: false},
  {key: "state", label: "State", required: false},
  {key: "postalCode", label: "Postal Code", required: false},
];
const BULK_USER_SAMPLES = [
  {fullName: "Anita Rao", email: "anita@company.com", phoneNumber: "+91 98450 00002", userRole: "CLIENT_MANAGER", city: "Bengaluru", state: "Karnataka", postalCode: "560035"},
  {fullName: "Vijay D", email: "vijay@company.com", phoneNumber: "+91 98450 00003", userRole: "Employee", city: "", state: "", postalCode: ""},
  {fullName: "Ramesh K", email: "", phoneNumber: "9845000004", userRole: "Employee", city: "", state: "", postalCode: ""},
];

/** Micro-badge tone per role — keeps the dense table scannable. */
const ROLE_PILL_TONE = {
  SUPER_ADMIN: "brand",
  CLIENT_ADMIN: "warning",
  CLIENT_MANAGER: "info",
  CLIENT_EMPLOYEE: "neutral",
  MOBI_TRACK: "success",
};

/**
 * Redesigned User List page (Phase 4 — Option A applied with the new
 * design system).
 *
 * Replaces the previous List + Register tab pair. The single page hosts:
 *   - Page header with primary CTA "+ New user"
 *   - Clickable KPI tiles (Total / Active / Suspended / Admins / Managers / Employees / Track Points)
 *     that filter the table when clicked.
 *   - Search + role select filter + active filter chips
 *   - Compact data table with Avatar + name + @username cell, role pill,
 *     status pill, and a kebab menu for row actions.
 *   - SlideOvers for Create / Edit, Modal for Password Reset.
 *
 * All existing permissions, APIs, and business logic are preserved.
 */
export default function UserList() {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // for KPI counts
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "", "ACTIVE", "SUSPENDED"
  const [roleFilter, setRoleFilter] = useState("");

  const [slideOver, setSlideOver] = useState({open: false, mode: "edit", user: null});
  const [resetTarget, setResetTarget] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const canEdit = hasPermission("users_edit");
  const canSuspend = hasPermission("users_suspend");
  const canTrack = hasPermission("users_enableTracking");
  const canReset = hasPermission("users_resetPassword");
  const canRegister = hasPermission("users_register");
  const canDelete = hasPermission("users_delete");
  const showActions = canEdit || canSuspend || canTrack || canReset || canDelete;

  // ── Data fetching ─────────────────────────────────────────────
  useEffect(() => {
    fetchUsers(1, perPage);
    fetchAllForStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (page, size) => {
    try {
      setIsLoading(true);
      setError(null);
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/${UrlConstants.GET_ALL_CLIENT_USERS}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(url, {params: buildPaginationParams(page, size)});
      if (res.status === 200 && Array.isArray(res.data.responseData)) {
        setUsers(res.data.responseData);
        const pagination = extractPaginationHeaders(res);
        setTotalRows(pagination ? pagination.totalCount : res.data.responseData.length);
      } else if (res.status === 403) {
        logoutFun();
      } else {
        setError("No data found");
      }
    } catch (e) {
      console.error("Error fetching users:", e);
      setError("Error loading users");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllForStats = async () => {
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/${UrlConstants.GET_ALL_CLIENT_USERS}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(url, {params: {page: 0, size: 1000}});
      if (res.status === 200 && Array.isArray(res.data.responseData)) {
        setAllUsers(res.data.responseData);
      }
    } catch {
      /* best-effort */
    }
  };

  // ── Mutations ─────────────────────────────────────────────────
  const toggleStatus = async (user) => {
    const wasSuspended = isSuspended(user);
    const expectedStatus = wasSuspended ? "activate" : "suspend";
    console.info("[Users] Status toggle requested →", {userId: user.userId, action: expectedStatus});
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/manage`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.post(url, {userId: user.userId, expectedStatus, loggedInUserId: getLoggedInUserId()});
      if (res.status === 200) {
        const nextStatus = wasSuspended ? "ACTIVE" : "SUSPENDED";
        setUsers((prev) => prev.map((u) => u.userId === user.userId ? {...u, userStatus: nextStatus} : u));
        setAllUsers((prev) => prev.map((u) => u.userId === user.userId ? {...u, userStatus: nextStatus} : u));
        showToast(wasSuspended ? "User activated" : "User suspended", {type: "success"});
      }
    } catch {
      showToast("Could not update user status", {type: "error"});
    }
  };

  // Admin-only soft delete — removes the user from all lists; record + history retained server-side.
  const deleteUser = async (user) => {
    if (!window.confirm(
      `Delete "${user.fullName || user.username}"?\n\nThe user will be removed from all lists and can no longer sign in. An admin can restore them later.`
    )) return;
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/manage`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.post(url, {userId: user.userId, expectedStatus: "delete", loggedInUserId: getLoggedInUserId()});
      if (res.status === 200) {
        setUsers((prev) => prev.filter((u) => u.userId !== user.userId));
        setAllUsers((prev) => prev.filter((u) => u.userId !== user.userId));
        showToast("User deleted", {type: "success"});
      }
    } catch (err) {
      showToast(err?.response?.data || "Could not delete user", {type: "error"});
    }
  };

  const toggleTrackable = async (user) => {
    console.info("[Users] Trackable toggle requested →", {userId: user.userId, trackable: !user.trackable});
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/${user.userId}/trackable`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.put(url, {trackable: !user.trackable, requesterId: getLoggedInUserId()});
      if (res.status === 200) {
        setUsers((prev) => prev.map((u) => u.userId === user.userId ? {...u, trackable: !user.trackable} : u));
        showToast(user.trackable ? "Tracking disabled" : "Tracking enabled", {type: "success"});
      }
    } catch {
      showToast("Could not update tracking", {type: "error"});
    }
  };

  const handleUserSaved = (next) => {
    if (slideOver.mode === "edit" && next?.userId) {
      setUsers((prev) => prev.map((u) => (u.userId === next.userId ? {...u, ...next} : u)));
    } else {
      // Refetch on create — backend assigns IDs, defaults, etc.
      fetchUsers(currentPage, perPage);
      fetchAllForStats();
    }
  };

  // Audit trail for filter interactions (KPI tiles, selects, search, chips)
  useEffect(() => {
    console.debug("[Users] Filters →", {status: statusFilter || "ALL", role: roleFilter || "ALL", search: search || "(none)"});
  }, [statusFilter, roleFilter, search]);

  // ── Stats (computed from allUsers) ────────────────────────────
  const stats = useMemo(() => {
    const total = allUsers.length;
    const active = allUsers.filter((u) => (u.userStatus || "").toUpperCase() === "ACTIVE").length;
    const suspended = total - active;
    const byRole = (r) => allUsers.filter((u) => u.userRole === r).length;
    return {
      total,
      active,
      suspended,
      admins: byRole("CLIENT_ADMIN"),
      managers: byRole("CLIENT_MANAGER"),
      employees: byRole("CLIENT_EMPLOYEE"),
      trackPoints: byRole("MOBI_TRACK"),
    };
  }, [allUsers]);

  // ── Client-side filtering on top of paged users ──────────────
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter && (u.userStatus || "").toUpperCase() !== statusFilter) return false;
      if (roleFilter && u.userRole !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.fullName || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phoneNumber || "").toLowerCase().includes(q) ||
        String(u.userId || "").includes(q)
      );
    });
  }, [users, search, statusFilter, roleFilter]);

  const activeFilters = [
    statusFilter && {label: `Status: ${statusFilter === "ACTIVE" ? "Active" : "Suspended"}`, onRemove: () => setStatusFilter("")},
    roleFilter && {label: `Role: ${ROLE_LABELS[roleFilter] || roleFilter}`, onRemove: () => setRoleFilter("")},
    search && {label: `Search: "${search}"`, onRemove: () => setSearch("")},
  ].filter(Boolean);

  const clearAll = () => {
    setStatusFilter("");
    setRoleFilter("");
    setSearch("");
  };

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      name: "User",
      grow: 1.6,
      minWidth: "240px",
      sortable: true,
      selector: (row) => row.fullName || row.username || "",
      cell: (row) => (
        <div className="flex items-center gap-3 py-1 min-w-0">
          <Avatar name={row.fullName || row.username} size="md" />
          <div className="min-w-0">
            <div className="font-medium text-ink-900 truncate" title={row.fullName || row.username}>
              {row.fullName || row.username}
            </div>
            <div className="text-xs text-ink-500 font-mono truncate">@{row.username} · ID {row.userId}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Role",
      width: "120px",
      sortable: true,
      selector: (row) => row.userRole,
      cell: (row) => (
        <Pill tone={ROLE_PILL_TONE[row.userRole] || "neutral"} size="sm">
          {ROLE_LABELS[row.userRole] || row.userRole || "—"}
        </Pill>
      ),
    },
    {
      name: "Email",
      grow: 1.2,
      minWidth: "200px",
      sortable: true,
      selector: (row) => row.email,
      cell: (row) => <span className="text-ink-600 truncate" title={row.email}>{row.email || "—"}</span>,
    },
    {
      name: "Phone",
      width: "140px",
      sortable: true,
      selector: (row) => row.phoneNumber,
      cell: (row) => <span className="text-ink-600">{row.phoneNumber || "—"}</span>,
    },
    {
      // Single date column: last edit if any, else registration. Hover shows both.
      name: "Updated",
      width: "150px",
      sortable: true,
      selector: (row) => row.updatedAt || row.uploadDate || "",
      cell: (row) => (
        <span className="text-xs text-ink-600" title={registeredUpdatedTitle(row.uploadDate, row.updatedAt)}>
          {formatDateTime(row.updatedAt || row.uploadDate) || "—"}
        </span>
      ),
    },
    {
      name: "Status",
      width: "120px",
      sortable: true,
      selector: (row) => row.userStatus,
      cell: (row) => {
        const suspended = isSuspended(row);
        return (
          <Pill tone={suspended ? "danger" : "success"} dot={!suspended} size="sm">
            {row.userStatus || "—"}
          </Pill>
        );
      },
    },
    ...(showActions ? [{
      name: "",
      width: "56px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      cell: (row) => {
        const suspended = isSuspended(row);
        return (
          <KebabMenu
            ariaLabel={`Actions for ${row.fullName || row.username}`}
            items={[
              {label: "Edit user", icon: Pencil, onClick: () => setSlideOver({open: true, mode: "edit", user: row}), hidden: !canEdit},
              {label: "Reset password", icon: Key, onClick: () => setResetTarget(row), hidden: !canReset},
              {
                label: row.trackable ? "Disable tracking" : "Enable tracking",
                icon: MapPinned,
                onClick: () => toggleTrackable(row),
                hidden: !canTrack,
              },
              {divider: true},
              {
                label: suspended ? "Activate user" : "Suspend user",
                icon: Power,
                danger: !suspended,
                onClick: () => toggleStatus(row),
                hidden: !canSuspend,
              },
              {
                label: "Delete user",
                icon: Trash2,
                danger: true,
                onClick: () => deleteUser(row),
                hidden: !canDelete,
              },
            ]}
          />
        );
      },
    }] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [showActions, canEdit, canReset, canTrack, canSuspend, canDelete]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage user accounts, roles, and access."
        actions={
          canRegister && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" icon={UploadCloud} onClick={() => setBulkUploadOpen(true)}>
                Bulk upload
              </Button>
              <Button variant="primary" icon={Plus} onClick={() => setSlideOver({open: true, mode: "create", user: null})}>
                New user
              </Button>
            </div>
          )
        }
      />

      {/* Full-width, tight vertical rhythm — the dense table is the hero */}
      <div className="px-4 lg:px-6 py-4 space-y-4">
        <KpiRow>
          <KpiTile
            label="Total users"
            value={stats.total}
            icon={UsersIcon}
            tone="brand"
            active={!statusFilter && !roleFilter}
            onClick={() => { setStatusFilter(""); setRoleFilter(""); }}
          />
          <KpiTile
            label="Active"
            value={stats.active}
            icon={CheckCircle2}
            tone="success"
            active={statusFilter === "ACTIVE"}
            onClick={() => setStatusFilter(statusFilter === "ACTIVE" ? "" : "ACTIVE")}
          />
          <KpiTile
            label="Suspended"
            value={stats.suspended}
            icon={XCircle}
            tone="danger"
            active={statusFilter === "SUSPENDED"}
            onClick={() => setStatusFilter(statusFilter === "SUSPENDED" ? "" : "SUSPENDED")}
          />
          <KpiTile
            label="Admins"
            value={stats.admins}
            icon={ShieldCheck}
            tone="info"
            active={roleFilter === "CLIENT_ADMIN"}
            onClick={() => setRoleFilter(roleFilter === "CLIENT_ADMIN" ? "" : "CLIENT_ADMIN")}
          />
          <KpiTile
            label="Managers"
            value={stats.managers}
            icon={UserCog}
            tone="warning"
            active={roleFilter === "CLIENT_MANAGER"}
            onClick={() => setRoleFilter(roleFilter === "CLIENT_MANAGER" ? "" : "CLIENT_MANAGER")}
          />
          <KpiTile
            label="Employees"
            value={stats.employees}
            icon={UserIcon}
            tone="neutral"
            active={roleFilter === "CLIENT_EMPLOYEE"}
            onClick={() => setRoleFilter(roleFilter === "CLIENT_EMPLOYEE" ? "" : "CLIENT_EMPLOYEE")}
          />
          <KpiTile
            label="Track Points"
            value={stats.trackPoints}
            icon={CircleDot}
            tone="success"
            active={roleFilter === "MOBI_TRACK"}
            onClick={() => setRoleFilter(roleFilter === "MOBI_TRACK" ? "" : "MOBI_TRACK")}
          />
        </KpiRow>

        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, email, phone, or ID…"
          chips={activeFilters}
          onClearAll={activeFilters.length ? clearAll : null}
        >
          <div className="w-40">
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All roles</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Select>
          </div>
          <div className="w-36">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
          </div>
        </FilterBar>

        <DataTable
          columns={columns}
          data={filteredUsers}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onChangePage={(page) => { setCurrentPage(page); fetchUsers(page, perPage); }}
          onChangeRowsPerPage={(size, page) => { setPerPage(size); setCurrentPage(page); fetchUsers(page, size); }}
          responsive
          highlightOnHover
          progressPending={isLoading}
          progressComponent={
            <div className="py-12 flex items-center justify-center gap-3 text-ink-500">
              <Spinner size="md" />
              <span className="text-sm">Loading users…</span>
            </div>
          }
          noDataComponent={
            <EmptyState
              icon={UsersIcon}
              tone={error ? "error" : "default"}
              title={
                error
                  ? "Could not load users"
                  : activeFilters.length
                  ? "No users match your filters"
                  : "No users yet"
              }
              description={
                error
                  ? typeof error === "string" ? error : "Please try again in a moment."
                  : activeFilters.length
                  ? "Try clearing some filters to widen the search."
                  : "Add your team to start tracking who has access to which assets."
              }
              action={
                error ? (
                  <Button variant="secondary" onClick={() => fetchUsers(currentPage, perPage)}>Try again</Button>
                ) : activeFilters.length ? (
                  <Button variant="secondary" onClick={clearAll}>Clear filters</Button>
                ) : (
                  canRegister && (
                    <Button variant="primary" icon={Plus} onClick={() => setSlideOver({open: true, mode: "create", user: null})}>
                      Add your first user
                    </Button>
                  )
                )
              }
            />
          }
          keyField="userId"
          customStyles={tableStyles}
        />
      </div>

      <UserSlideOver
        open={slideOver.open}
        mode={slideOver.mode}
        user={slideOver.user}
        onClose={() => setSlideOver({open: false, mode: "edit", user: null})}
        onSaved={handleUserSaved}
      />
      <ResetPasswordSlideOver
        open={!!resetTarget}
        user={resetTarget}
        onClose={() => setResetTarget(null)}
      />

      <BulkUploadSlideOver
        entity="users"
        title="Bulk upload users"
        subtitle="Import team members from a CSV or Excel file."
        columns={BULK_USER_COLUMNS}
        sampleRows={BULK_USER_SAMPLES}
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        commitRow={async (row) => {
          const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.USERS}`;
          axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
          // Same envelope as the single "New user" flow (UserSlideOver)
          await axios.post(url, {
            requestId: generateRandomNumber().toString(),
            clientId: getClientIdOfUser(),
            userId: getLoggedInUserId(),
            requestBody: {
              ...row,
              email: row.email || null,
              phoneNumber: row.phoneNumber || null,
              // Blank username -> backend derives it (email, else name.phone).
              username: row.email || null,
              userStatus: "ACTIVE",
            },
          });
        }}
        onDone={({ok}) => {
          if (ok > 0) {
            showToast(`${ok} user${ok === 1 ? "" : "s"} imported`, {type: "success"});
            fetchUsers(1, perPage);
            fetchAllForStats();
          }
        }}
      />
    </>
  );
}
